import { csvMoney, csvRate } from '../csvValues';
import { csv, number, text } from '../RmMonitor/liveModel';

export const PAGE_ID = 'accommodation-views-impressions-sales';
export const VIEWS = [
  'SPP | Conversion',
  'SPP | Supplier Breakdown',
  'LPC | Performance by Campaign',
  'LPC | Tactical Campaigns Detail',
] as const;

export type ViewId = (typeof VIEWS)[number];

export const CARDS = {
  conversion: 'spp-conversion',
  supplier: 'spp-supplier',
  lpc: 'lpc-performance',
  tactical: 'lpc-tactical',
} as const;

/**
 * Yesterday in Sydney. The Tableau workbook's Current Period End Date moves forward a day each day with no
 * republish (19 Oct 2026 when the defaults were captured on 20 Oct, 20 Oct on 21 Oct), so the default follows it.
 */
export function sydneyYesterday(now = new Date()): string {
  const [year, month, day] = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Australia/Sydney',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .format(now)
    .split('-')
    .map(Number);
  return new Date(Date.UTC(year, month - 1, day - 1)).toISOString().slice(0, 10);
}

export const DEFAULT_FILTERS = {
  cp_start: '2026-07-01',
  cp_end: sydneyYesterday(),
  pp_start: '2026-06-01',
  pp_end: '2026-06-23',
  comparison: 'Custom',
  margin_max: 'Exclude',
  conv_scope: 'CP only',
  rate_position: 'All',
  conv_split1: 'SPP Property Name',
  conv_split2: 'Product Name',
  sup_split1: 'Supplier',
  sup_split2: 'All',
  lpc_split1: 'Vendor Name',
  lpc_split2: 'Tactical Campaign Sources',
} as const;

export type LensFilters = { [K in keyof typeof DEFAULT_FILTERS]: string };

export type GrainRow = Record<string, unknown>;

type Totals = {
  impressions: number;
  packages: number;
  pageViews: number;
  margin: number;
  ttv: number;
  atc: number;
  gmAr: number;
  ttvAr: number;
};

export type PeriodPair<T> = { current: T; previous: T };

export type ConversionTableRow = {
  period: string;
  split1: string;
  split2: string;
  impressions: number;
  packages: number;
  pageViews: number;
  margin: number;
  ttv: number;
  atc: number;
};

export type DayPoint = { day: string; period: string; value: number };

export type SupplierTableRow = {
  period: string;
  split1: string;
  split2: string;
  impressions: number;
  margin: number;
  ttv: number;
  items: number;
};

export type SupplierShares = {
  lpc: number | null;
  lpp: number | null;
  lpcLead: number | null;
  lppLead: number | null;
  impressions: number;
};

export type LpcTableRow = {
  period: string;
  split1: string;
  split2: string;
  pageViews: number;
  impressions: number;
  lppImpressions: number;
  packages: number;
  margin: number;
  ttv: number;
};

export type LpcKpis = {
  impressions: number;
  pageViews: number;
  packages: number;
  cr: number | null;
  share: number | null;
  impressionCr: number | null;
  margin: number;
};

export type TacticalRow = {
  vendor: string;
  source: string;
  campaign: string;
  promotionLength: string;
  campaignStart: string;
  campaignEnd: string;
  priorStart: string;
  priorEnd: string;
  keep: boolean;
  ttvDuring: number;
  ttvPrior: number;
  itemsDuring: number;
  itemsPrior: number;
  marginDuring: number;
  marginPrior: number;
  priorComplete: string;
  spoofed: string;
  runTime: number;
  periodPrior: number;
  impressionsDuring: number;
  impressionsPrior: number;
  lppImpressionsDuring: number;
  lppImpressionsPrior: number;
};

const num = (row: GrainRow, key: string) => number(row[key]) ?? 0;
const str = (row: GrainRow, key: string) => {
  const value = text(row[key]);
  return value === 'Unknown' ? '' : value;
};

export function inclusiveDays(from: string, to: string): number {
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return 0;
  return Math.round((end - start) / 86_400_000) + 1;
}

export function previousWindow(filters: LensFilters): { from: string; to: string } {
  if (filters.comparison !== 'Last Year') return { from: filters.pp_start, to: filters.pp_end };
  return { from: shiftYear(filters.cp_start, -1), to: shiftYear(filters.cp_end, -1) };
}

function shiftYear(iso: string, years: number): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  const [year, month, day] = iso.split('-').map(Number);
  // BigQuery DATE_SUB clamps leap day to the last day of February in the target year.
  const targetYear = year + years;
  const targetDay = Math.min(day, new Date(Date.UTC(targetYear, month, 0)).getUTCDate());
  return `${targetYear}-${String(month).padStart(2, '0')}-${String(targetDay).padStart(2, '0')}`;
}

export function formatInteger(value: number): string {
  return new Intl.NumberFormat('en-AU', { maximumFractionDigits: 0 }).format(Math.round(value));
}

export function formatPercent(value: number | null, digits = 2): string {
  if (value === null || !Number.isFinite(value)) return '';
  return `${(value * 100).toFixed(digits)}%`;
}

export function formatSignedPercent(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '';
  const body = `${(Math.abs(value) * 100).toFixed(2)}%`;
  if (value > 0) return `+${body}`;
  if (value < 0) return `-${body}`;
  return body;
}

export function formatMoney(value: number, digits: number): string {
  const rounded = digits === 0 ? Math.round(value) : value;
  const body = new Intl.NumberFormat('en-AU', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Math.abs(rounded));
  return rounded < 0 ? `-$${body}` : `$${body}`;
}

export function change(current: number, previous: number): number | null {
  if (!previous) return null;
  return current / previous - 1;
}

export function ratio(part: number, whole: number): number | null {
  if (!whole) return null;
  return part / whole;
}

function byKind(rows: readonly GrainRow[], kind: string): GrainRow[] {
  return rows.filter((row) => str(row, 'kind') === kind);
}

function pairTotals(rows: readonly GrainRow[]): PeriodPair<Totals> {
  const read = (period: string): Totals => {
    const row = rows.find((item) => str(item, 'period') === period);
    return {
      impressions: row ? num(row, 'impressions') : 0,
      packages: row ? num(row, 'packages') : 0,
      pageViews: row ? num(row, 'page_views') : 0,
      margin: row ? num(row, 'margin') : 0,
      ttv: row ? num(row, 'ttv') : 0,
      atc: row ? num(row, 'atc') : 0,
      gmAr: row ? num(row, 'gm_ar') : 0,
      ttvAr: row ? num(row, 'ttv_ar') : 0,
    };
  };
  return { current: read('Current Period'), previous: read('Previous Period') };
}

export function conversionModel(rows: readonly GrainRow[]) {
  const totals = pairTotals(byKind(rows, 'total'));
  const table: ConversionTableRow[] = byKind(rows, 'row').map((row) => ({
    period: str(row, 'period'),
    split1: str(row, 'split1'),
    split2: str(row, 'split2'),
    impressions: num(row, 'impressions'),
    packages: num(row, 'packages'),
    pageViews: num(row, 'page_views'),
    margin: num(row, 'margin'),
    ttv: num(row, 'ttv'),
    atc: num(row, 'atc'),
  }));
  const days = byKind(rows, 'day').map((row) => ({
    day: str(row, 'day'),
    period: str(row, 'period'),
    impressions: num(row, 'impressions'),
  }));
  return { totals, table: sortConversion(table), days };
}

export function sortConversion(rows: readonly ConversionTableRow[]): ConversionTableRow[] {
  const weight = new Map<string, number>();
  rows.forEach((row) => {
    if (row.period !== 'Current Period') return;
    const key = row.split1;
    weight.set(key, (weight.get(key) ?? 0) + row.impressions);
  });
  return [...rows].sort((left, right) => {
    const period = left.period.localeCompare(right.period);
    if (period) return period;
    const byMeasure = (weight.get(right.split1) ?? 0) - (weight.get(left.split1) ?? 0);
    if (byMeasure) return byMeasure;
    const name = left.split1.localeCompare(right.split1);
    if (name) return name;
    return left.split2.localeCompare(right.split2);
  });
}

export function conversionKpis(totals: PeriodPair<Totals>) {
  const tile = (current: number, previous: number) => ({ current, previous, change: change(current, previous) });
  const currentCr = ratio(totals.current.packages, totals.current.pageViews);
  const previousCr = ratio(totals.previous.packages, totals.previous.pageViews);
  const currentMp = ratio(totals.current.margin, totals.current.impressions);
  const previousMp = ratio(totals.previous.margin, totals.previous.impressions);
  return {
    impressions: tile(totals.current.impressions, totals.previous.impressions),
    packages: tile(totals.current.packages, totals.previous.packages),
    cr: { current: currentCr, previous: previousCr, change: currentCr !== null && previousCr ? currentCr / previousCr - 1 : null },
    mppoi: { current: currentMp, previous: previousMp, change: currentMp !== null && previousMp ? currentMp / previousMp - 1 : null },
    margin: tile(totals.current.margin, totals.previous.margin),
    ttv: tile(totals.current.ttv, totals.previous.ttv),
  };
}

export const CONVERSION_CSV_COLUMNS = [
  'Time Split',
  'Data Split | Offer Views',
  'Data Split 2 | Offer Views',
  'Package Option Impressions',
  'Packages',
  'Impression CR',
  'Page View CR',
  'MpPOI',
  'GM (BR)',
  'TTV (BR)',
] as const;

export function conversionCsv(rows: readonly ConversionTableRow[]): string {
  return csv(
    rows.map((row) => ({
      'Time Split': 'Full Period',
      'Data Split | Offer Views': row.split1,
      'Data Split 2 | Offer Views': row.split2,
      'Package Option Impressions': row.impressions,
      Packages: row.packages,
      'Impression CR': csvRate(ratio(row.packages, row.impressions)),
      'Page View CR': csvRate(ratio(row.packages, row.pageViews)),
      MpPOI: csvRate(ratio(row.margin, row.impressions)),
      'GM (BR)': csvMoney(row.margin),
      'TTV (BR)': csvMoney(row.ttv),
    })),
    [...CONVERSION_CSV_COLUMNS],
  );
}

export function conversionDays(days: readonly { day: string; period: string; impressions: number }[], period: string): DayPoint[] {
  return days
    .filter((row) => row.period === period)
    .map((row) => ({ day: row.day, period: row.period, value: row.impressions }))
    .sort((left, right) => left.day.localeCompare(right.day));
}

export function supplierModel(rows: readonly GrainRow[]) {
  const lifetime = byKind(rows, 'lifetime')[0];
  const shares: SupplierShares = {
    impressions: lifetime ? num(lifetime, 'impressions') : 0,
    lpc: lifetime ? ratio(num(lifetime, 'lpc_impressions'), num(lifetime, 'lpc_impressions') + num(lifetime, 'lpp_impressions')) : null,
    lpp: lifetime ? ratio(num(lifetime, 'lpp_impressions'), num(lifetime, 'lpc_impressions') + num(lifetime, 'lpp_impressions')) : null,
    lpcLead: lifetime ? ratio(num(lifetime, 'lpc_lead'), num(lifetime, 'lpc_impressions')) : null,
    lppLead: lifetime ? ratio(num(lifetime, 'lpp_lead'), num(lifetime, 'lpp_impressions')) : null,
  };
  const table: SupplierTableRow[] = byKind(rows, 'row').map((row) => ({
    period: str(row, 'period'),
    split1: str(row, 'split1'),
    split2: str(row, 'split2'),
    impressions: num(row, 'impressions'),
    margin: num(row, 'margin'),
    ttv: num(row, 'ttv'),
    items: num(row, 'items'),
  }));
  const days = byKind(rows, 'day')
    .filter((row) => str(row, 'period') === 'Current Period')
    .map((row) => ({ day: str(row, 'day'), period: str(row, 'period'), value: num(row, 'impressions') }))
    .sort((left, right) => left.day.localeCompare(right.day));
  return { shares, table: sortSupplier(table), days };
}

export function sortSupplier(rows: readonly SupplierTableRow[]): SupplierTableRow[] {
  const weight = new Map<string, number>();
  rows.forEach((row) => {
    if (row.period !== 'Current Period') return;
    weight.set(row.split1, (weight.get(row.split1) ?? 0) + row.impressions);
  });
  return [...rows].sort((left, right) => {
    const split2 = left.split2.localeCompare(right.split2);
    if (split2) return split2;
    const byMeasure = (weight.get(right.split1) ?? 0) - (weight.get(left.split1) ?? 0);
    if (byMeasure) return byMeasure;
    return left.split1.localeCompare(right.split1);
  });
}

export function supplierCsv(rows: readonly SupplierTableRow[]): string {
  return csv(
    rows.map((row) => ({
      'Data Split | Suppliers': row.split1,
      'Data Split 2 | Suppliers': row.split2,
      Impressions: row.impressions,
      'GM (BR)': csvMoney(row.margin),
      'TTV (BR)': csvMoney(row.ttv),
      Items: row.items,
    })),
    ['Data Split | Suppliers', 'Data Split 2 | Suppliers', 'Impressions', 'GM (BR)', 'TTV (BR)', 'Items'],
  );
}

export function lpcModel(rows: readonly GrainRow[]) {
  const read = (period: string): LpcKpis => {
    const row = byKind(rows, 'total').find((item) => str(item, 'period') === period);
    const impressions = row ? num(row, 'lpc_rate_impressions') : 0;
    const lpp = row ? num(row, 'lpp_rate_impressions') : 0;
    const packages = row ? num(row, 'packages') : 0;
    const pageViews = row ? num(row, 'page_views') : 0;
    return {
      impressions,
      pageViews,
      packages,
      cr: ratio(packages, pageViews),
      share: ratio(impressions, impressions + lpp),
      impressionCr: ratio(packages, impressions),
      margin: row ? num(row, 'margin_net') : 0,
    };
  };
  const table: LpcTableRow[] = byKind(rows, 'row').map((row) => ({
    period: str(row, 'period'),
    split1: str(row, 'split1'),
    split2: str(row, 'split2'),
    pageViews: num(row, 'page_views'),
    impressions: num(row, 'lpc_rate_impressions'),
    lppImpressions: num(row, 'lpp_rate_impressions'),
    packages: num(row, 'packages'),
    margin: num(row, 'margin_net'),
    ttv: num(row, 'ttv'),
  }));
  const days = byKind(rows, 'day')
    .filter((row) => str(row, 'period') === 'Current Period')
    .map((row) => ({ day: str(row, 'day'), period: 'Current Period', value: num(row, 'lpc_rate_impressions') }))
    .sort((left, right) => left.day.localeCompare(right.day));
  return { current: read('Current Period'), previous: read('Previous Period'), table: sortLpc(table), days };
}

export function sortLpc(rows: readonly LpcTableRow[]): LpcTableRow[] {
  const weight = new Map<string, number>();
  rows.forEach((row) => {
    if (row.period !== 'Current Period') return;
    weight.set(row.split1, (weight.get(row.split1) ?? 0) + row.pageViews);
  });
  return [...rows].sort((left, right) => {
    const byMeasure = (weight.get(right.split1) ?? 0) - (weight.get(left.split1) ?? 0);
    if (byMeasure) return byMeasure;
    const name = left.split1.localeCompare(right.split1);
    if (name) return name;
    return left.split2.localeCompare(right.split2);
  });
}

export function lpcCsv(rows: readonly LpcTableRow[]): string {
  return csv(
    rows.map((row) => ({
      'Data Split 1 | LPC': row.split1,
      'Data Split 2 | LPC': row.split2,
      'Page Views': row.pageViews,
      'LPC Rate Impressions': row.impressions,
      'LPC Impression Share': csvRate(ratio(row.impressions, row.impressions + row.lppImpressions)),
      Packages: row.packages,
      'CR (Packages/Page Views)': csvRate(ratio(row.packages, row.pageViews)),
      'CR (Packages/Impressions)': csvRate(ratio(row.packages, row.impressions)),
      'GM AR': csvMoney(row.margin),
      'TTV (AR)': csvMoney(row.ttv),
    })),
    [
      'Data Split 1 | LPC',
      'Data Split 2 | LPC',
      'Page Views',
      'LPC Rate Impressions',
      'LPC Impression Share',
      'Packages',
      'CR (Packages/Page Views)',
      'CR (Packages/Impressions)',
      'GM AR',
      'TTV (AR)',
    ],
  );
}

const TACTICAL_MEASURES = [
  'ttvDuring',
  'ttvPrior',
  'itemsDuring',
  'itemsPrior',
  'marginDuring',
  'marginPrior',
  'runTime',
  'periodPrior',
  'impressionsDuring',
  'impressionsPrior',
  'lppImpressionsDuring',
  'lppImpressionsPrior',
] as const;

export function tacticalRows(rows: readonly GrainRow[]): { kpis: TacticalRow[]; detail: TacticalRow[] } {
  const mapped = rows.map((row) => ({
    priorComplete: str(row, 'prior_window_complete'),
    spoofed: str(row, 'extranet_was_spoofed'),
    runTime: num(row, 'run_time'),
    periodPrior: num(row, 'period_prior'),
    impressionsDuring: num(row, 'impressions_during'),
    impressionsPrior: num(row, 'impressions_prior'),
    lppImpressionsDuring: num(row, 'lpp_impressions_during'),
    lppImpressionsPrior: num(row, 'lpp_impressions_prior'),
    vendor: str(row, 'vendor_name'),
    source: str(row, 'campaign_source'),
    campaign: str(row, 'campaign_name'),
    promotionLength: str(row, 'promotion_length'),
    campaignStart: str(row, 'campaign_start'),
    campaignEnd: str(row, 'campaign_end'),
    priorStart: str(row, 'prior_start'),
    priorEnd: str(row, 'prior_end'),
    keep: str(row, 'keep_in_sheet') === 'true',
    ttvDuring: num(row, 'ttv_during'),
    ttvPrior: num(row, 'ttv_prior'),
    itemsDuring: num(row, 'items_during'),
    itemsPrior: num(row, 'items_prior'),
    marginDuring: num(row, 'margin_during'),
    marginPrior: num(row, 'margin_prior'),
  }));
  const grouped = new Map<string, TacticalRow>();
  for (const row of mapped) {
    const key = JSON.stringify([
      row.vendor,
      row.source,
      row.campaign,
      row.promotionLength,
      row.campaignStart,
      row.campaignEnd,
      row.priorStart,
      row.priorEnd,
      row.priorComplete,
      row.spoofed,
      row.keep,
    ]);
    const previous = grouped.get(key);
    if (!previous) {
      grouped.set(key, { ...row });
      continue;
    }
    for (const measure of TACTICAL_MEASURES) {
      previous[measure] += row[measure];
    }
  }
  const detail = [...grouped.values()]
    .filter((row) => row.keep)
    .sort(
      (left, right) =>
        left.vendor.localeCompare(right.vendor) ||
        left.source.localeCompare(right.source) ||
        left.campaign.localeCompare(right.campaign) ||
        left.campaignStart.localeCompare(right.campaignStart),
    );
  return { kpis: mapped, detail };
}

export function tacticalTotals(rows: readonly TacticalRow[]) {
  return rows.reduce(
    (sum, row) => ({
      ttvDuring: sum.ttvDuring + row.ttvDuring,
      ttvPrior: sum.ttvPrior + row.ttvPrior,
      itemsDuring: sum.itemsDuring + row.itemsDuring,
      itemsPrior: sum.itemsPrior + row.itemsPrior,
      marginDuring: sum.marginDuring + row.marginDuring,
      marginPrior: sum.marginPrior + row.marginPrior,
    }),
    { ttvDuring: 0, ttvPrior: 0, itemsDuring: 0, itemsPrior: 0, marginDuring: 0, marginPrior: 0 },
  );
}

export function tacticalGrandTotal(rows: ReadonlyArray<TacticalRow>): TacticalRow {
  const total: TacticalRow = {
    vendor: 'Grand Total',
    source: '',
    campaign: '',
    promotionLength: '',
    campaignStart: '',
    campaignEnd: '',
    priorStart: '',
    priorEnd: '',
    keep: true,
    priorComplete: '',
    spoofed: '',
    ttvDuring: 0,
    ttvPrior: 0,
    itemsDuring: 0,
    itemsPrior: 0,
    marginDuring: 0,
    marginPrior: 0,
    runTime: 0,
    periodPrior: 0,
    impressionsDuring: 0,
    impressionsPrior: 0,
    lppImpressionsDuring: 0,
    lppImpressionsPrior: 0,
  };
  for (const row of rows) {
    for (const measure of TACTICAL_MEASURES) {
      total[measure] += row[measure];
    }
  }
  return total;
}

const MARGIN_RATIO_SCALE = [
  { ratio: 0, rgb: [250, 143, 121] },
  { ratio: 0.65, rgb: [255, 173, 156] },
  { ratio: 1, rgb: [218, 209, 208] },
  { ratio: 1.4, rgb: [168, 206, 230] },
  { ratio: 5, rgb: [46, 90, 135] },
] as const;
const MARGIN_RATIO_NEW_SALES = 5;
const LIGHT_TEXT_BELOW_LUMA = 140;

export function tacticalMarginColour(row: TacticalRow): { backgroundColor: string; color: string } {
  const marginRatio = row.marginPrior
    ? row.marginDuring / row.marginPrior
    : row.marginDuring > 0
      ? MARGIN_RATIO_NEW_SALES
      : 0;
  const value = Math.min(MARGIN_RATIO_NEW_SALES, Math.max(0, marginRatio));
  const upper = Math.max(
    1,
    MARGIN_RATIO_SCALE.findIndex(({ ratio: stop }) => stop >= value),
  );
  const low = MARGIN_RATIO_SCALE[upper - 1];
  const high = MARGIN_RATIO_SCALE[upper];
  const share = (value - low.ratio) / (high.ratio - low.ratio);
  const [red, green, blue] = low.rgb.map((channel, index) => Math.round(channel + (high.rgb[index] - channel) * share));
  const luma = 0.2126 * red + 0.7152 * green + 0.0722 * blue;
  return {
    backgroundColor: `rgb(${red}, ${green}, ${blue})`,
    color: luma < LIGHT_TEXT_BELOW_LUMA ? '#fff' : 'rgba(0, 0, 0, 0.87)',
  };
}

export function tacticalColumns(row?: TacticalRow) {
  return [
    { label: 'Vendor Name', value: row?.vendor, format: 'text' },
    { label: 'Campaign Source', value: row?.source, format: 'text' },
    { label: 'Campaign Name', value: row?.campaign, format: 'text' },
    { label: 'Prior Window Complete', value: row?.priorComplete, format: 'text' },
    { label: 'Extranet Was Spoofed', value: row?.spoofed, format: 'text' },
    { label: 'Promotion Length', value: row?.promotionLength, format: 'text' },
    { label: 'Campaign Start', value: row?.campaignStart, format: 'text' },
    { label: 'Campaign End', value: row?.campaignEnd, format: 'text' },
    { label: 'Prior Start', value: row?.priorStart, format: 'text' },
    { label: 'Prior End', value: row?.priorEnd, format: 'text' },
    { label: 'Run Time', value: row?.runTime, format: 'integer' },
    { label: 'Period Prior', value: row?.periodPrior, format: 'integer' },
    { label: 'Margin During', value: row?.marginDuring, format: 'money' },
    { label: 'Margin Prior', value: row?.marginPrior, format: 'money' },
    { label: 'Margin Ratio', value: ratio(row?.marginDuring ?? 0, row?.marginPrior ?? 0), format: 'rate' },
    { label: 'Items During', value: row?.itemsDuring, format: 'integer' },
    { label: 'Items Prior', value: row?.itemsPrior, format: 'integer' },
    { label: 'Items Ratio', value: ratio(row?.itemsDuring ?? 0, row?.itemsPrior ?? 0), format: 'rate' },
    { label: 'LPC Impressions During', value: row?.impressionsDuring, format: 'integer' },
    { label: 'LPC Impressions Prior', value: row?.impressionsPrior, format: 'integer' },
    {
      label: 'Impression CVR During',
      value: ratio(row?.itemsDuring ?? 0, row?.impressionsDuring ?? 0),
      format: 'conversion',
    },
    {
      label: 'Impression CVR Prior',
      value: ratio(row?.itemsPrior ?? 0, row?.impressionsPrior ?? 0),
      format: 'conversion',
    },
    {
      label: 'Margin per Impression During',
      value: ratio(row?.marginDuring ?? 0, row?.impressionsDuring ?? 0),
      format: 'unitMoney',
    },
    {
      label: 'Margin per Impression Prior',
      value: ratio(row?.marginPrior ?? 0, row?.impressionsPrior ?? 0),
      format: 'unitMoney',
    },
    {
      label: 'LPC Impression Share During',
      value: ratio(row?.impressionsDuring ?? 0, (row?.impressionsDuring ?? 0) + (row?.lppImpressionsDuring ?? 0)),
      format: 'rate',
    },
    {
      label: 'LPC Impression Share Prior',
      value: ratio(row?.impressionsPrior ?? 0, (row?.impressionsPrior ?? 0) + (row?.lppImpressionsPrior ?? 0)),
      format: 'rate',
    },
    { label: 'TTV During', value: row?.ttvDuring, format: 'money' },
    { label: 'TTV Prior', value: row?.ttvPrior, format: 'money' },
  ] as const;
}

export function tacticalDisplayRow(row: TacticalRow): Array<string> {
  return tacticalColumns(row).map(({ value, format }) => {
    if (value == null) return '—';
    if (typeof value !== 'number') return value;
    if (format === 'rate' || format === 'conversion') return formatPercent(value, format === 'conversion' ? 2 : 1);
    if (format === 'money' || format === 'unitMoney') return formatMoney(value, format === 'unitMoney' ? 2 : 0);
    return formatInteger(value);
  });
}

export function tacticalCsv(rows: readonly TacticalRow[]): string {
  const records = rows.map((row) =>
    Object.fromEntries(
      tacticalColumns(row).map(({ label, value, format }) => [
        label,
        typeof value !== 'number'
          ? value
          : format === 'rate' || format === 'conversion'
            ? csvRate(value)
            : format === 'money' || format === 'unitMoney'
              ? csvMoney(value)
              : value,
      ]),
    ),
  );
  return csv(
    records,
    tacticalColumns().map(({ label }) => label),
  );
}

export function cardRows(cards: ReadonlyArray<{ cardId?: string; rows?: ReadonlyArray<GrainRow> }> | undefined, id: string): GrainRow[] {
  return [...(cards?.find((card) => card.cardId === id)?.rows ?? [])];
}
